"use client";
import { useState, useMemo } from "react";
import Link from "next/link";
import {
  MapPin,
  Clock,
  Calendar,
  Compass,
  Camera,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  ThermometerSun,
  Droplets,
  Snowflake,
} from "lucide-react";
import { cleanHeaderTags, createLookupObject, decodeHtml } from "@/lib/helper";
import { ICMS } from "@/store/home/homeModel";
import { OptimizedImage } from "@/components/common/OptimizedImage";

interface AttractionDetailClientProps {
  attractionUrl: string;
  attractionsData: any;
  cmsData: ICMS[];
}

export default function AttractionDetail({
  attractionUrl,
  attractionsData,
  cmsData,
}: AttractionDetailClientProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const lookUpCMSData = useMemo(() => {
    return createLookupObject(cmsData || [], "Name");
  }, [cmsData]);

  const AttractionsDetail = attractionsData;
  const SliderImages = AttractionsDetail
    ? [
        { ID: 0, Image: AttractionsDetail.BannerImage },
        ...AttractionsDetail.Images,
      ]
    : [];

  const nextImage = () =>
    setCurrentImageIndex((prev) => (prev + 1) % SliderImages.length);
  const prevImage = () =>
    setCurrentImageIndex(
      (prev) => (prev - 1 + SliderImages.length) % SliderImages.length,
    );

  if (!AttractionsDetail) {
    return (
      <div className="min-h-screen py-32 text-center">
        Attraction not found.
      </div>
    );
  }

  return (
    <>
      {/* Hero Image Slider */}
      <section className="relative h-[80vh] overflow-hidden mt-24">
        <div className="relative w-full h-full">
          <OptimizedImage
            src={SliderImages[currentImageIndex]?.Image}
            alt={`${AttractionsDetail.Title} - Image ${currentImageIndex + 1}`}
            width={1200}
            height={800}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 backdrop-blur-md p-3 rounded-full transition-all z-20"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 backdrop-blur-md p-3 rounded-full transition-all z-20"
            aria-label="Next image"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 z-10">
            <div className="max-w-7xl mx-auto">
              <h1 className="text-white mb-4">{AttractionsDetail.Title}</h1>
              <p className="text-white/90 text-2xl font-light">
                {AttractionsDetail.TagLine}
              </p>
            </div>
          </div>
          <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-2 z-20">
            {SliderImages?.map((_: any, index: number) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-3 h-3 rounded-full transition-all ${index === currentImageIndex ? "bg-white w-8" : "bg-white/50"}`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Quick Info Bar */}
      <div className="sticky top-24 z-40 bg-white/95 backdrop-blur-lg border-y border-[#D8C4A8]/30 shadow-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-[#7A4A1D]" />
              <div>
                <p className="text-xs text-[#5A3A1E]/85">Distance</p>
                <p className="font-semibold text-[#5A3A1E]">
                  {AttractionsDetail?.Distance}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-[#7A4A1D]" />
              <div>
                <p className="text-xs text-[#5A3A1E]/85">Travel Time</p>
                <p className="font-semibold text-[#5A3A1E]">
                  {AttractionsDetail?.TravelTime}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-[#7A4A1D]" />
              <div>
                <p className="text-xs text-[#5A3A1E]/85">Best Time</p>
                <p className="font-semibold text-[#5A3A1E]">
                  {AttractionsDetail?.BestTime}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Compass className="w-5 h-5 text-[#7A4A1D]" />
              <div>
                <p className="text-xs text-[#5A3A1E]/85">Duration</p>
                <p className="font-semibold text-[#5A3A1E]">
                  {AttractionsDetail?.Duration}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Camera className="w-5 h-5 text-[#7A4A1D]" />
              <div>
                <p className="text-xs text-[#5A3A1E]/85">Category</p>
                <p className="font-semibold text-[#5A3A1E]">
                  {AttractionsDetail?.AttractionCategory}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* About Section */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="mb-8 text-[#5A3A1E]">
            About {AttractionsDetail?.Title}
          </h2>
          <p
            className="text-[#5A3A1E]/85 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: cleanHeaderTags(AttractionsDetail?.Description),
            }}
          ></p>
        </div>
      </section>

      {/* Why Visit */}
      {AttractionsDetail?.WhyVisit?.length > 0 && (
        <section className="py-20 px-6 lg:px-8 bg-[#F3EBDD]">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-center mb-16 text-[#5A3A1E]">
              Why Visit This Place
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {AttractionsDetail?.WhyVisit?.map(
                (reason: any, index: number) => (
                  <div
                    key={index}
                    className="flex gap-4 p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all"
                  >
                    <div className="w-12 h-12 bg-[#7A4A1D] rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#5A3A1E] mb-2">
                        {reason?.Title}
                      </h3>
                      <p className="text-[#5A3A1E]/85">{reason?.Description}</p>
                    </div>
                  </div>
                ),
              )}
            </div>
          </div>
        </section>
      )}

      {/* How to Reach */}
      {AttractionsDetail?.HowToReach?.length > 0 && (
        <section className="py-20 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="mb-12 text-[#5A3A1E]">How to Reach</h2>
            <div className="space-y-6">
              {AttractionsDetail?.HowToReach?.map(
                (reach: any, index: number) => (
                  <div
                    key={index}
                    className="p-6 bg-gradient-to-r from-[#F3EBDD] to-white rounded-2xl shadow-lg"
                  >
                    <h3 className="text-xl font-bold text-[#7A4A1D] mb-3">
                      {reach.Title}
                    </h3>
                    <p className="text-[#5A3A1E]/85">{reach.Description}</p>
                  </div>
                ),
              )}
            </div>
          </div>
        </section>
      )}

      {/* Best Time to Visit */}
      <section className="py-20 px-6 lg:px-8 bg-gradient-to-b from-white to-[#F3EBDD]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-center mb-16 text-[#5A3A1E]">
            Best Time to Visit
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {AttractionsDetail.BestTimes.map((season: any, index: number) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg text-center"
              >
                <div
                  className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${season?.SeasonType === "Winter" ? "bg-gray-100" : season?.SeasonType === "Summer" ? "bg-red-100" : season?.SeasonType === "Monsoon" ? "bg-blue-100" : "bg-green-100"}`}
                >
                  {season?.SeasonType === "All Season" && (
                    <Check className="w-10 h-10 text-green-600" />
                  )}
                  {season?.SeasonType === "Winter" && (
                    <Snowflake className="w-10 h-10 text-gray-600" />
                  )}
                  {season?.SeasonType === "Summer" && (
                    <ThermometerSun className="w-10 h-10 text-red-600" />
                  )}
                  {season?.SeasonType === "Monsoon" && (
                    <Droplets className="w-10 h-10 text-blue-600" />
                  )}
                </div>
                <h3 className="text-xl font-bold text-[#5A3A1E] mb-2">
                  {season?.SeasonType}
                </h3>
                <span
                  className={`inline-block px-4 py-1 rounded-full text-sm font-semibold mb-3 ${season?.SeasonType === "Winter" ? "bg-gray-100 text-gray-700" : season?.SeasonType === "Summer" ? "bg-red-100 text-red-700" : season?.SeasonType === "Monsoon" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}
                >
                  {season.Title}
                </span>
                <p className="text-[#5A3A1E]/85">{season.Description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Things to Do */}
      {AttractionsDetail?.ThingsToDo?.length > 0 && (
        <section className="py-20 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="mb-12 text-[#5A3A1E]">Things to Do There</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {AttractionsDetail?.ThingsToDo.map(
                (activity: any, index: number) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-4 bg-[#F3EBDD] rounded-xl"
                  >
                    <Check className="w-5 h-5 text-[#7A4A1D] flex-shrink-0" />
                    <span className="text-[#5A3A1E]">
                      {activity.Description}
                    </span>
                  </div>
                ),
              )}
            </div>
          </div>
        </section>
      )}

      {/* Image Gallery */}
      {AttractionsDetail?.Images?.length > 0 && (
        <section className="py-20 px-6 lg:px-8 bg-[#F3EBDD]">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-center mb-16 text-[#5A3A1E]">Photo Gallery</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {AttractionsDetail?.Images?.map((image: any, index: number) => (
                <div
                  key={index}
                  className="aspect-square overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all group"
                >
                  <OptimizedImage
                    src={image?.Image}
                    alt={`Gallery ${index + 1}`}
                    width={600}
                    height={400}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Travel Tips */}
      {(AttractionsDetail?.Dos || AttractionsDetail?.Donts) && (
        <section className="py-20 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-center mb-16 text-[#5A3A1E]">Travel Tips</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {AttractionsDetail?.Dos && (
                <div className="bg-green-50 p-8 rounded-2xl shadow-lg">
                  <h3 className="text-2xl font-bold text-green-800 mb-6 flex items-center gap-2">
                    <Check className="w-7 h-7" />
                    Do's
                  </h3>
                  <div
                    className="cms-section Dos-list"
                    dangerouslySetInnerHTML={{
                      __html: cleanHeaderTags(
                        decodeHtml(AttractionsDetail?.Dos),
                      ),
                    }}
                  />
                </div>
              )}
              {AttractionsDetail?.Donts && (
                <div className="bg-red-50 p-8 rounded-2xl shadow-lg">
                  <h3 className="text-2xl font-bold text-red-800 mb-6 flex items-center gap-2">
                    <X className="w-7 h-7" />
                    Don'ts
                  </h3>
                  <div
                    className="Donts-list cms-section"
                    dangerouslySetInnerHTML={{
                      __html: cleanHeaderTags(
                        decodeHtml(AttractionsDetail?.Donts),
                      ),
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {lookUpCMSData?.["Attractions - Detail  page CTA"] && (
        <div
          dangerouslySetInnerHTML={{
            __html: cleanHeaderTags(
              decodeHtml(
                lookUpCMSData?.["Attractions - Detail  page CTA"]?.Template,
              ),
            ),
          }}
        />
      )}

      {/* Nearby Attractions */}
      {AttractionsDetail?.Nearby?.length > 0 && (
        <section className="py-20 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-center mb-16 text-[#5A3A1E]">
              Nearby Attractions
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {AttractionsDetail?.Nearby?.map((nearby: any) => (
                <Link
                  key={nearby.SelectedAttractionID}
                  href={`/attractions/${nearby.AttractionURL}/`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <OptimizedImage
                      src={nearby.MainImage || "#"}
                      alt={nearby.Title || "Nearby Attraction"}
                      width={600}
                      height={400}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#5A3A1E] mb-2">
                      {nearby.Title}
                    </h3>
                    <p className="text-[#BF5500] font-semibold flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {nearby.KmAwayFromMainAttraction} km away
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
